import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Plus, Sparkles, Pencil } from 'lucide-react';
import NovaNegociacaoAutomaticaModal, { AutomaticoResult } from './NovaNegociacaoAutomaticaModal';
import { useAuth } from '../../../src/contexts/AuthContext';

interface NegociacaoFABProps {
    onInsertData: () => void;
    /** Chamado quando o fluxo automático (documento + fatura) termina e os dados estão prontos para prefill da negociação. */
    onAutomaticoReady?: (result: AutomaticoResult) => void;
}

const NegociacaoFAB: React.FC<NegociacaoFABProps> = ({ onInsertData, onAutomaticoReady }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isAutomaticoModalOpen, setIsAutomaticoModalOpen] = useState(false);
    const { profile } = useAuth();
    // parceiro/vendedor_parceiro têm a barra inferior fixa no mobile (Perfil/Financeiro/Bancário) —
    // sobe o FAB pra não ficar em cima dela
    const temBarraInferior = profile?.role === 'parceiro' || profile?.role === 'vendedor_parceiro';

    return createPortal(
        <>
            {/* z-40: fica abaixo do menu lateral mobile (z-50 em App.tsx), pra não sobrepor o drawer quando aberto */}
            <div className={`fixed ${temBarraInferior ? 'bottom-24 md:bottom-8' : 'bottom-8'} right-8 z-40 flex flex-col items-end gap-3`}>
                {isOpen && (
                    <div className="flex flex-col items-end gap-3 mb-2 animate-fade-in-up">
                        <button
                            onClick={() => {
                                onInsertData();
                                setIsOpen(false);
                            }}
                            className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-200 font-semibold rounded-lg shadow-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition-all border border-gray-100 dark:border-slate-700"
                        >
                            <span className="text-sm">Manualmente</span>
                            <div className="w-8 h-8 rounded-full bg-primary/10 dark:bg-primary/20 flex items-center justify-center text-primary">
                                <Pencil size={16} />
                            </div>
                        </button>

                        <button
                            onClick={() => {
                                setIsAutomaticoModalOpen(true);
                                setIsOpen(false);
                            }}
                            className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-200 font-semibold rounded-lg shadow-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition-all border border-gray-100 dark:border-slate-700"
                        >
                            <span className="text-sm">Automático</span>
                            <div className="w-8 h-8 rounded-full bg-orange-100 dark:bg-orange-500/20 flex items-center justify-center text-orange-600 dark:text-orange-400">
                                <Sparkles size={16} />
                            </div>
                        </button>
                    </div>
                )}

                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className={`group flex items-center gap-2 pl-4 pr-2 py-2 rounded-full shadow-xl transition-all duration-300 ${isOpen ? 'bg-primary-dark text-white' : 'bg-primary text-white hover:bg-primary-dark'
                        }`}
                >
                    <span className="font-bold text-sm">Nova Negociação</span>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-transform duration-300 ${isOpen ? 'bg-slate-700 rotate-45' : 'bg-white/20'
                        }`}>
                        <Plus size={20} />
                    </div>
                </button>
            </div>

            <NovaNegociacaoAutomaticaModal
                isOpen={isAutomaticoModalOpen}
                onClose={() => setIsAutomaticoModalOpen(false)}
                onReady={(result) => {
                    setIsAutomaticoModalOpen(false);
                    setIsOpen(false);
                    onAutomaticoReady?.(result);
                }}
            />
        </>,
        document.body
    );
};

export default NegociacaoFAB;
